import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryError, setRecoveryError] = useState(null);
  const [recoverySuccess, setRecoverySuccess] = useState(null);
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLogin = location.pathname === '/login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    const action = isLogin ? signIn : signUp;
    const { error } = await action(email, password);
    if (error) {
      setFormError(error.message);
      return;
    }
    navigate('/dashboard');
  };

  const mapRecoveryErrorToSpanish = (message) => {
    if (!message) return 'No se pudo enviar el correo de recuperación.';
    const lower = message.toLowerCase();
    if (lower.includes('invalid email')) return 'Ingresa un correo electrónico válido.';
    if (lower.includes('rate limit'))
      return 'Has solicitado demasiados correos. Intenta de nuevo en unos minutos.';
    return 'No se pudo enviar el enlace de recuperación. Intenta nuevamente.';
  };

  const handlePasswordRecovery = async (e) => {
    e.preventDefault();
    setRecoveryError(null);
    setRecoverySuccess(null);
    const targetEmail = recoveryEmail.trim();
    if (!targetEmail) {
      setRecoveryError('Ingresa tu correo electrónico para continuar.');
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(targetEmail, {
      redirectTo: 'https://latinosenwallstreet.com/reset-password',
    });
    if (error) {
      setRecoveryError(mapRecoveryErrorToSpanish(error.message));
      return;
    }
    setRecoverySuccess('Te enviamos un enlace de recuperación a tu correo');
    setRecoveryEmail('');
  };

  return (
    <div className="max-w-md mx-auto mt-12">
      <Card className="p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 text-brand-red mb-4">
            <SafeIcon icon={FiIcons.FiLock} className="text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-brand-dark">
            {isLogin ? 'Bienvenido de vuelta' : 'Crea tu cuenta'}
          </h2>
          <p className="text-brand-slate mt-2">
            {isLogin
              ? 'Ingresa tus datos para continuar.'
              : 'Únete a la comunidad de inversores latinos.'}
          </p>
        </div>

        {formError && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {formError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Correo Electrónico
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-brand-red focus:border-brand-red outline-none transition"
              placeholder="tu@email.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-brand-red focus:border-brand-red outline-none transition"
              placeholder="••••••••"
            />
          </div>
          {isLogin && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => {
                  setShowRecovery((prev) => !prev);
                  setRecoveryError(null);
                  setRecoverySuccess(null);
                }}
                className="text-sm text-brand-red font-medium hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          )}
          <Button type="submit" className="w-full py-2.5 mt-2">
            {isLogin ? 'Iniciar Sesión' : 'Registrarse'}
          </Button>
        </form>

        {isLogin && showRecovery && (
          <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
            <p className="text-sm text-gray-700 mb-3">
              Ingresa tu correo para enviarte un enlace de recuperación.
            </p>
            {recoveryError && (
              <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {recoveryError}
              </div>
            )}
            {recoverySuccess && (
              <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
                {recoverySuccess}
              </div>
            )}
            <form onSubmit={handlePasswordRecovery} className="space-y-3">
              <input
                type="email"
                required
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-brand-red focus:border-brand-red outline-none transition bg-white"
                placeholder="tu@email.com"
              />
              <Button type="submit" className="w-full py-2">
                Enviar enlace de recuperación
              </Button>
            </form>
          </div>
        )}

        <div className="mt-6 text-center text-sm text-gray-500">
          {isLogin ? (
            <p>
              ¿No tienes cuenta?{' '}
              <Link
                to="/register"
                className="text-brand-red font-medium hover:underline"
              >
                Regístrate aquí
              </Link>
            </p>
          ) : (
            <p>
              ¿Ya tienes cuenta?{' '}
              <Link
                to="/login"
                className="text-brand-red font-medium hover:underline"
              >
                Inicia sesión
              </Link>
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}
